/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  ADD YOUR VIDEOS HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *  Paste a normal YouTube, Loom or Vimeo link — the share URL straight from
 *  the address bar is fine. Supported shapes:
 *
 *    YouTube  https://www.youtube.com/watch?v=VIDEO_ID
 *             https://youtu.be/VIDEO_ID
 *             https://www.youtube.com/shorts/VIDEO_ID
 *    Loom     https://www.loom.com/share/VIDEO_ID
 *    Vimeo    https://vimeo.com/VIDEO_ID  (or vimeo.com/VIDEO_ID/HASH if unlisted)
 *
 *  `poster` is optional. YouTube, Loom and Vimeo thumbnails are fetched
 *  automatically; set `poster` only to override them (e.g. a custom frame, or
 *  a video whose auto-thumbnail does not load). Import a local image from ../assets
 *  and pass it, or use an absolute URL.
 *
 *  The section hides itself completely while this list is empty, so the live
 *  site never shows an empty shelf.
 */
export const videos: VideoItem[] = [
    {
        title: 'Previous Work Portfolio',
        description: 'A run-through of the automation systems we have delivered for clients.',
        url: 'https://youtu.be/MTsmztDNGHA',
    },
    {
        title: 'End-to-End WhatsApp RAG Agent in n8n',
        description:
            'Building a retrieval-augmented WhatsApp agent from scratch in n8n, start to finish.',
        url: 'https://www.loom.com/share/ca533b348f1a447485860fa6cc64dbc6',
    },
    {
        title: 'AI Call Sales Agent for a Real Estate Company',
        description: 'A voice agent handling sales calls for a real-estate client.',
        url: 'https://youtu.be/Dm-xoUS2fsQ',
    },
    {
        title: 'Streamlining E-Commerce Order Processing',
        description: 'How an automated workflow takes e-commerce orders through to fulfilment.',
        url: 'https://youtu.be/pkAIL7OK1mk',
    },
    {
        title: 'End to End AI sales chatbot (Whatsapp)',
        description: 'Inteligent AI sales chatbot from lead nurturing through to fulfilment.',
        url: 'https://youtube.com/shorts/ocEx3W-uclo?si=OnrWii78xj_8jxMZ',
    },
    
    {
        title: 'Order Processing with API Integration and Reporting',
        description:
            'Pulling orders from an API, matching them against customer records in Airtable, and generating sales-by-region reports.',
        url: 'https://www.loom.com/share/2ad178c8e3f44d76a810ec80d83ac1db',
    },
    {
        title: 'AI Telegram & WhatsApp Agent for Housing Support',
        description: 'An AI agent answering housing support requests across Telegram and WhatsApp.',
        url: 'https://www.loom.com/share/dae5708b91054e56b639572b070a08b0',
    },
    {
        title: 'Chatbot Back Office: 24/7 Ticketing Support',
        description: 'The back office behind a support chatbot that raises and manages tickets around the clock.',
        url: 'https://www.loom.com/share/3657ca597d6a42358d59c96863842047',
    },
];

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  REAL ESTATE DEMO VIDEO  (top of the /real-estate page)
 * ─────────────────────────────────────────────────────────────────────────────
 *  A YouTube or Vimeo link, or a file in /public such as '/real-estate-demo.mp4'.
 */
export const VIDEO_URL = 'https://youtu.be/iRhzfPnRKXQ';
/** Optional thumbnail, e.g. '/real-estate-demo.jpg'. Empty: the video's own thumbnail, else a gradient. */
export const POSTER_URL = '';

export interface VideoItem {
    title: string;
    description?: string;
    /** YouTube, Loom or Vimeo URL. */
    url: string;
    /** Optional thumbnail override. */
    poster?: string;
}

export type VideoProvider = 'youtube' | 'loom' | 'vimeo';

export interface ParsedVideo {
    provider: VideoProvider;
    id: string;
    /** Player URL for the iframe, set to start playing on load. */
    embedUrl: string;
    /** Auto-derived thumbnail, or null when the provider has none. */
    thumbnailUrl: string | null;
    /** Lower-resolution fallback used if `thumbnailUrl` 404s. */
    fallbackThumbnailUrl: string | null;
    /** oEmbed endpoint to resolve the thumbnail from when it can't be derived. */
    oembedUrl: string | null;
    label: string;
}

const YOUTUBE_PATTERNS = [
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/watch\?(?:.*&)?v=([\w-]{11})/,
    /youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})/,
    /youtube\.com\/shorts\/([\w-]{11})/,
    /youtube\.com\/live\/([\w-]{11})/,
];

const LOOM_PATTERNS = [/loom\.com\/(?:share|embed)\/([\w-]+)/];

/** `player.vimeo.com/video/ID?h=HASH`, or `vimeo.com/[channels/…/]ID[/HASH]`. */
const VIMEO_PLAYER_PATTERN = /player\.vimeo\.com\/video\/(\d+)(?:.*[?&]h=([\da-f]+))?/;
const VIMEO_PAGE_PATTERN = /vimeo\.com\/(?:.*\/)?(\d+)(?:\/([\da-f]+))?(?:[/?#]|$)/;

function matchFirst(url: string, patterns: RegExp[]): string | null {
    for (const pattern of patterns) {
        const m = pattern.exec(url);
        if (m) return m[1];
    }
    return null;
}

/**
 * Turns a share URL into everything the player needs. Returns null for links we
 * don't recognise, so a typo hides that one card instead of breaking the page.
 */
export function parseVideoUrl(url: string): ParsedVideo | null {
    const youtubeId = matchFirst(url, YOUTUBE_PATTERNS);
    if (youtubeId) {
        return {
            provider: 'youtube',
            id: youtubeId,
            embedUrl: `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`,
            thumbnailUrl: `https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`,
            fallbackThumbnailUrl: `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`,
            oembedUrl: null,
            label: 'YouTube',
        };
    }

    const loomId = matchFirst(url, LOOM_PATTERNS);
    if (loomId) {
        return {
            provider: 'loom',
            id: loomId,
            embedUrl: `https://www.loom.com/embed/${loomId}?autoplay=1&hideEmbedTopBar=true`,
            // Loom thumbnail filenames carry an unguessable content hash, so the
            // URL is resolved at runtime via `fetchOembedThumbnail` instead.
            thumbnailUrl: null,
            fallbackThumbnailUrl: null,
            oembedUrl: `https://www.loom.com/v1/oembed?url=https://www.loom.com/share/${loomId}`,
            label: 'Loom',
        };
    }

    const vimeo = VIMEO_PLAYER_PATTERN.exec(url) ?? VIMEO_PAGE_PATTERN.exec(url);
    if (vimeo) {
        const [, vimeoId, hash] = vimeo;
        const pageUrl = `https://vimeo.com/${vimeoId}${hash ? `/${hash}` : ''}`;
        return {
            provider: 'vimeo',
            id: vimeoId,
            // `dnt` stops the player setting tracking cookies, like youtube-nocookie.
            embedUrl: `https://player.vimeo.com/video/${vimeoId}?autoplay=1&dnt=1${hash ? `&h=${hash}` : ''}`,
            thumbnailUrl: null,
            fallbackThumbnailUrl: null,
            oembedUrl: `https://vimeo.com/api/oembed.json?width=1280&url=${encodeURIComponent(pageUrl)}`,
            label: 'Vimeo',
        };
    }

    return null;
}

/**
 * YouTube answers `maxresdefault.jpg` with a 120x90 grey placeholder (HTTP 200,
 * so `onError` never fires) for videos that have no max-res frame. Anything that
 * small is a miss and should fall back to `hqdefault`.
 */
export function isPlaceholderThumbnail(img: HTMLImageElement): boolean {
    return img.naturalWidth > 0 && img.naturalWidth <= 120;
}

/**
 * Resolves a video's real thumbnail through the provider's public oEmbed
 * endpoint (Loom's and Vimeo's are CORS-open). Returns null on any failure so
 * the caller can fall back to the gradient placeholder rather than showing a
 * broken image.
 */
export async function fetchOembedThumbnail(oembedUrl: string, signal?: AbortSignal): Promise<string | null> {
    try {
        const response = await fetch(oembedUrl, { signal });
        if (!response.ok) return null;
        const data = (await response.json()) as { thumbnail_url?: unknown };
        return typeof data.thumbnail_url === 'string' ? data.thumbnail_url : null;
    } catch {
        return null;
    }
}
